import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";
import Logo from "../assets/imgs/logo_lord.png";

const statusMap = ["Pending", "Completed", "Canceled"];
const statusColor = ["#facc15", "#22c55e", "#ef4444"];

const styles = StyleSheet.create({
    page: {
        backgroundColor: "#fff",
        color: "#222",
        fontSize: 12,
        fontFamily: "Helvetica",
        padding: 32,
        maxWidth: 600,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 18,
        borderBottom: "1 solid #eee",
        paddingBottom: 12,
    },
    logoBox: { flexDirection: "row", alignItems: "center", gap: 8 },
    logo: { width: 40, height: 40, marginRight: 8 },
    company: { fontWeight: "bold", fontSize: 16 },
    url: { color: "#888", fontSize: 10 },
    title: { fontSize: 22, fontWeight: "bold", marginBottom: 2 },
    status: (status) => ({
        backgroundColor: statusColor[status],
        color: "#222",
        fontWeight: "bold",
        fontSize: 10,
        borderRadius: 8,
        padding: "2 10",
        alignSelf: "flex-end",
        marginTop: 4,
        marginBottom: 2,
        minWidth: 70,
        textAlign: "center",
    }),
    section: { flexDirection: "row", justifyContent: "space-between", marginBottom: 12 },
    col: { flex: 1 },
    label: { color: "#888", fontSize: 10, marginBottom: 2 },
    value: { fontWeight: "bold", fontSize: 12, marginBottom: 2 },
    table: { border: "1 solid #eee", borderRadius: 6, marginBottom: 12 },
    row: { flexDirection: "row", borderBottom: "1 solid #eee" },
    cell: { flex: 1, padding: 6, fontSize: 11 },
    cellHeader: { backgroundColor: "#f3f4f6", fontWeight: "bold" },
    cellRight: { textAlign: "right" },
    footer: { textAlign: "center", color: "#888", fontSize: 10, marginTop: 24 },
});

const formatDate = (date) => (date ? new Date(date).toLocaleString() : "---");
const formatMoney = (amount, currency = "USD") =>
    `${amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const DepositInvoicePDF = ({ deposit, user }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            {/* HEADER */}
            <View style={styles.header}>
                <View style={styles.logoBox}>
                    <Image src={Logo} style={styles.logo} />
                    <View>
                        <Text style={styles.company}>AK API</Text>
                        <Text style={styles.url}>https://akng.io.vn</Text>
                    </View>
                </View>
                <View>
                    <Text style={styles.title}>Deposit Invoice</Text>
                    <Text style={styles.status(deposit.status)}>{statusMap[deposit.status] || "Unknown"}</Text>
                </View>
            </View>

            {/* CUSTOMER + META */}
            <View style={styles.section}>
                <View style={styles.col}>
                    <Text style={styles.label}>Billed To</Text>
                    <Text style={styles.value}>{user.display_name || user.username}</Text>
                    <Text style={styles.label}>User ID: {user.id}</Text>
                </View>
                <View style={styles.col}>
                    <Text style={styles.label}>Invoice ID</Text>
                    <Text style={styles.value}>#{deposit.id}</Text>
                    <Text style={styles.label}>Created: {formatDate(deposit.created_at)}</Text>
                    {deposit.verified_at && (
                        <Text style={styles.label}>Verified: {formatDate(deposit.verified_at)}</Text>
                    )}
                </View>
            </View>

            {/* INVOICE SUMMARY */}
            <View style={styles.table}>
                <View style={[styles.row, styles.cellHeader]}>
                    <Text style={styles.cell}>Invoice ID</Text>
                    <Text style={[styles.cell, styles.cellRight]}>#{deposit.id}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.cell}>Created At</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{formatDate(deposit.created_at)}</Text>
                </View>
                <View style={[styles.row]}>
                    <Text style={[styles.cell]}>Request Amount</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{formatMoney(deposit.received_amount, deposit.trans_currency)}</Text>
                </View>
                <View style={[styles.row, { backgroundColor: "#f9fafb" }]}>
                    <Text style={[styles.cell, { fontWeight: "bold" }]}>Status</Text>
                    <Text style={[styles.cell, styles.cellRight, { fontWeight: "bold" }]}>{statusMap[deposit.status]}</Text>
                </View>
            </View>

            {/* TRANSACTION DETAILS */}
            <View style={styles.table}>
                <View style={[styles.row, styles.cellHeader]}>
                    <Text style={styles.cell}>Payment Method</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{deposit.method?.name || deposit.method_callback}</Text>
                </View>
                <View style={[styles.row]}>
                    <Text style={styles.cell}>Address/Number</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{deposit.method?.address || "--"}</Text>
                </View>
                <View style={[styles.row]}>
                    <Text style={styles.cell}>Recipient Name</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{deposit.method?.recipient || "--"}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.cell}>Transaction Amount</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{formatMoney(deposit.trans_amount, deposit.trans_currency)}</Text>
                </View>
                {deposit.trans_id && 
                <View style={styles.row}>
                    <Text style={styles.cell}>Transaction ID</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{deposit.trans_id}</Text>
                </View>}
                {deposit.sender_name &&
                <View style={styles.row}>
                    <Text style={styles.cell}>Sender Name</Text>
                    <Text style={[styles.cell, styles.cellRight]}>{deposit.sender_name}</Text>
                </View>
                }
            </View>

            {/* BALANCE (SUCCESS ONLY) */}
            {deposit.status === 1 && (
                <View style={styles.table}>
                    <View style={[styles.row]}>
                        <Text style={styles.cell}>Balance Before</Text>
                        <Text style={[styles.cell, styles.cellRight]}>{formatMoney(deposit.before_balance, deposit.trans_currency)}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.cell}>Balance After</Text>
                        <Text style={[styles.cell, styles.cellRight]}>{formatMoney(deposit.after_balance, deposit.trans_currency)}</Text>
                    </View>
                </View>
            )}

            {/* FOOTER */}
            <Text style={styles.footer}>© {new Date().getFullYear()} AK API</Text>
        </Page>
    </Document>
);

export default DepositInvoicePDF;
